import React, { useEffect, useRef, useState } from 'react';
import {
  createCommunicationRequestId,
  isSmsConfirmationPending
} from '../utils/communicationRequestId';
import { formatCommunicationStatus } from '../utils/communicationStatus';

function FaxSender() {
  const [faxNumber, setFaxNumber] = useState('');
  const [file, setFile] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('fax'); // 'fax' or 'sms'
  const [smsPendingConfirmation, setSmsPendingConfirmation] = useState(false);
  const smsSendLockRef = useRef(false);
  const smsRequestIdRef = useRef(null);

  useEffect(() => {
    smsRequestIdRef.current = null;
    setSmsPendingConfirmation(false);
  }, [phoneNumber, message]);

  const handleSendFax = async (e) => {
    e.preventDefault();
    setStatus('');
    setLoading(true);

    if (!file) {
      setStatus('Please select a file to fax');
      setLoading(false);
      return;
    }

    try {
      // Convert file to base64
      const fileData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = reader.result.split(',')[1]; // Remove data:...;base64, prefix
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const formData = new FormData();
      formData.append('fax_number', faxNumber.trim());
      formData.append('file_data', fileData);
      formData.append('file_name', file.name);

      const response = await fetch('https://api.casedb.co/fax-fixed.php', {
        method: 'POST',
        credentials: 'include', // Required for session-based auth
        body: formData,
      });

      const result = await response.json();
      if (response.ok && result.success) {
        console.log('Fax sent successfully', result);
        const faxId = result.fax_id || 'unknown';
        setStatus(`Fax sent successfully! (ID: ${faxId})`);
        setFaxNumber('');
        setFile(null);
        // Reset file input
        document.getElementById('file').value = '';
      } else {
        console.error('Fax error:', result.error || result);
        setStatus(`Fax failed: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Network error:', err);
      setStatus('Fax failed: Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMS = async (e) => {
    e.preventDefault();
    if (smsSendLockRef.current || smsPendingConfirmation) return;

    smsSendLockRef.current = true;
    setStatus('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('phone_number', phoneNumber.trim());
      formData.append('message', message.trim());
      const requestId = smsRequestIdRef.current || createCommunicationRequestId();
      smsRequestIdRef.current = requestId;
      formData.append('request_id', requestId);

      const response = await fetch('https://api.casedb.co/fax-fixed.php', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      const result = await response.json();
      if (isSmsConfirmationPending(result)) {
        setSmsPendingConfirmation(true);
        setStatus(formatCommunicationStatus({
          type: 'texts',
          metadata: { delivery_status: 'confirmation_pending' }
        }).text);
      } else if (response.ok && result.success) {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
        if (result.queued) {
          setStatus(formatCommunicationStatus({
            type: 'texts',
            metadata: { delivery_status: 'queued', queued_until: result.scheduled_for }
          }).text);
        } else {
          console.log('SMS sent successfully', result);
          const messageId = result.message_id || 'unknown';
          setStatus(`SMS sent successfully! (ID: ${messageId})`);
        }
        setPhoneNumber('');
        setMessage('');
      } else {
        if (result.retry_with_new_request_id) {
          smsRequestIdRef.current = null;
        }
        console.error('SMS error:', result.error || result);
        let errorMessage = result.error || 'Unknown error';
        
        // Show more specific error messages based on common issues
        if (errorMessage.includes('not available')) {
          errorMessage = 'SMS feature not enabled for this account. Please contact support.';
        } else if (errorMessage.includes('Permission')) {
          errorMessage = 'SMS permission denied. Account may not have SMS capabilities.';
        } else if (errorMessage.includes('phoneNumber')) {
          errorMessage = 'Invalid phone number or unauthorized sender number.';
        }
        
        setStatus(`SMS failed: ${errorMessage}`);
      }
    } catch (err) {
      console.error('Network error:', err);
      setStatus('SMS failed: Network error');
    } finally {
      smsSendLockRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h2 className="communication-header">Communication Center</h2>
      
      {/* Tab Navigation */}
      <div className="tab-navigation" style={{ marginBottom: '20px' }}>
        <button 
          type="button"
          className={`tab-button ${activeTab === 'sms' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('sms');
            setStatus('');
          }}
          style={{
            padding: '10px 20px',
            marginRight: '10px',
            border: '1px solid #ddd',
            backgroundColor: activeTab === 'sms' ? '#007cba' : '#f8f9fa',
            color: activeTab === 'sms' ? 'white' : '#333',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Send SMS
        </button>
        <button 
          type="button"
          className={`tab-button ${activeTab === 'fax' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('fax');
            setStatus('');
          }}
          style={{
            padding: '10px 20px',
            border: '1px solid #ddd',
            backgroundColor: activeTab === 'fax' ? '#007cba' : '#f8f9fa',
            color: activeTab === 'fax' ? 'white' : '#333',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Send Fax
        </button>
      </div>

      {/* SMS Form */}
      {activeTab === 'sms' && (
        <form onSubmit={handleSendSMS}>
          <div className="form-group">
            <label htmlFor="phoneNumber">Phone Number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              placeholder="+17132213838"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              className="form-control"
              rows="4"
              style={{ resize: 'vertical' }}
              maxLength="1000"
            />
            <small style={{ 
              color: message.length > 160 ? '#e74c3c' : message.length > 140 ? '#f39c12' : '#666', 
              fontSize: '12px' 
            }}>
              {message.length}/1000 characters 
              {message.length > 160 && (
                <span style={{ color: '#e74c3c' }}>
                  {' '}(Will be sent as {Math.ceil(message.length / 160)} messages)
                </span>
              )}
            </small>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={!phoneNumber || !message || loading || smsPendingConfirmation}
            style={{
              backgroundColor: loading || smsPendingConfirmation ? '#ccc' : '#007cba',
              cursor: loading || smsPendingConfirmation ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Sending...' : smsPendingConfirmation ? 'Checking delivery...' : 'Send SMS'}
          </button>
        </form>
      )}

      {/* Fax Form */}
      {activeTab === 'fax' && (
        <form onSubmit={handleSendFax}>
          <div className="form-group">
            <label htmlFor="faxNumber">Fax Number</label>
            <input
              type="tel"
              id="faxNumber"
              name="faxNumber"
              placeholder="+17132213838"
              value={faxNumber}
              onChange={(e) => setFaxNumber(e.target.value)}
              required
              className="form-control"
            />
          </div>

          <div className="form-group">
            <label htmlFor="file">File to Fax</label>
            <input
              type="file"
              id="file"
              name="file"
              accept=".pdf,.tiff,.txt,.doc,.docx,.jpg,.png"
              onChange={(e) => setFile(e.target.files[0])}
              required
              className="form-control"
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              Supported formats: PDF, TIFF, TXT, DOC, DOCX, JPG, PNG
            </small>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={!faxNumber || !file || loading}
            style={{
              backgroundColor: loading ? '#ccc' : '#007cba',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Sending...' : 'Send Fax'}
          </button>
        </form>
      )}

      {/* Status Message */}
      {status && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          borderRadius: '4px',
          backgroundColor: smsPendingConfirmation ? '#fff3cd' : status.includes('sent successfully') ? '#d4edda' : '#f8d7da',
          border: smsPendingConfirmation ? '1px solid #ffeeba' : status.includes('sent successfully') ? '1px solid #c3e6cb' : '1px solid #f5c6cb',
          color: smsPendingConfirmation ? '#856404' : status.includes('sent successfully') ? '#155724' : '#721c24'
        }}>
          {status}
        </div>
      )}
    </div>
  );
}

export default FaxSender;
